import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CheckoutTemplate,
  CheckoutTemplateCreatedEnvelope,
  CheckoutTemplateDeletedEnvelope,
  CheckoutTemplateQuery,
  CheckoutTemplateUpdatedEnvelope,
  CheckoutTemplateUsedEnvelope,
  CheckoutTemplatesQueryBuilder,
  CreateAndRedirectToCheckoutApplicationErrors,
  CreateCheckoutFromTemplateApplicationErrors,
  CreateCheckoutFromTemplateResponse,
  CreateCheckoutTemplateApplicationErrors,
  QueryCheckoutTemplatesResponse,
  RawHttpResponse,
  UpdateCheckoutTemplate,
  UpdateCheckoutTemplateApplicationErrors,
  createAndRedirectToCheckout as universalCreateAndRedirectToCheckout,
  createCheckoutFromTemplate as universalCreateCheckoutFromTemplate,
  createCheckoutTemplate as universalCreateCheckoutTemplate,
  deleteCheckoutTemplate as universalDeleteCheckoutTemplate,
  getCheckoutTemplate as universalGetCheckoutTemplate,
  queryCheckoutTemplates as universalQueryCheckoutTemplates,
  typedQueryCheckoutTemplates as universalTypedQueryCheckoutTemplates,
  updateCheckoutTemplate as universalUpdateCheckoutTemplate,
} from './ecom-v1-checkout-template-checkout-templates.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createCheckoutTemplate(
  httpClient: HttpClient
): CreateCheckoutTemplateSignature {
  return (
    checkoutTemplate: NonNullablePaths<
      CheckoutTemplate,
      | `customLineItems.${number}.descriptionLines.${number}.name`
      | `customLineItems.${number}.itemType`
      | `customLineItems.${number}.productName`
      | `customLineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.quantity`,
      6
    >
  ) =>
    universalCreateCheckoutTemplate(
      checkoutTemplate,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCheckoutTemplateSignature {
  /**
   * Creates a checkout template.
   *
   * A checkout template is used to create a new checkout that will include predefined information. For example, a single link with
   * a `checkoutTemplateId` can be shared with customers and each time the link is clicked, a new checkout page will be created
   * for that customer with certain checkout information already populated.
   *
   * The customizable features include the option to allow or to lock coupon codes or gift cards. For example, if a store owner is
   * using the checkout template to offer a flash sale to their social media followers, they may want to lock the option to apply an
   * additional coupon on top of the sale being offered. If so, they can set `customization.lockedCoupon` to `true`.
   *
   * A checkout can be created with a checkout template by calling Create Checkout From Template.
   * The site may add further customizations to the new checkout and then redirect the customer using the new checkout's `checkoutUrl`.
   * @param - Checkout template to create.
   * @returns Created checkout template.
   */
  (
    checkoutTemplate: NonNullablePaths<
      CheckoutTemplate,
      | `customLineItems.${number}.descriptionLines.${number}.name`
      | `customLineItems.${number}.itemType`
      | `customLineItems.${number}.productName`
      | `customLineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.quantity`,
      6
    >
  ): Promise<
    NonNullablePaths<
      CheckoutTemplate,
      | `status`
      | `customization.webClient.disabledContinueShoppingButton`
      | `customization.webClient.disabledPolicyAgreementCheckbox`
      | `customization.lockedGiftCard`
      | `customization.lockedCoupon`
      | `customization.disabledManualPayment`
      | `lineItems`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogOverrideFields.productName.original`
      | `lineItems.${number}.catalogOverrideFields.physicalProperties.shippable`
      | `lineItems.${number}.catalogOverrideFields.paymentOption.value`
      | `customLineItems`
      | `customLineItems.${number}.quantity`
      | `customLineItems.${number}.price`
      | `customLineItems.${number}.priceDescription.original`
      | `customLineItems.${number}.itemType.preset`
      | `customLineItems.${number}.itemType.custom`
      | `customLineItems.${number}.paymentOption`
      | `customLineItems.${number}.priceUndetermined`
      | `customLineItems.${number}.fixedQuantity`
      | `customLineItems.${number}.savePaymentMethod`
      | `customLineItems.${number}.membersOnly`
      | `customLineItems.${number}.taxableAddress.addressType`
      | `customContentReference.appId`
      | `customContentReference.componentId`
      | `externalReference.appId`,
      6
    > & {
      __applicationErrorsType?: CreateCheckoutTemplateApplicationErrors;
    }
  >;
}

export function getCheckoutTemplate(
  httpClient: HttpClient
): GetCheckoutTemplateSignature {
  return (checkoutTemplateId: string) =>
    universalGetCheckoutTemplate(
      checkoutTemplateId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCheckoutTemplateSignature {
  /**
   * Retrieves a checkout template.
   * @param - ID of the checkout template to retrieve.
   * @returns Retrieved checkout template.
   */
  (checkoutTemplateId: string): Promise<
    NonNullablePaths<
      CheckoutTemplate,
      | `status`
      | `customization.webClient.disabledContinueShoppingButton`
      | `customization.webClient.disabledPolicyAgreementCheckbox`
      | `customization.lockedGiftCard`
      | `customization.lockedCoupon`
      | `customization.disabledManualPayment`
      | `lineItems`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogOverrideFields.productName.original`
      | `lineItems.${number}.catalogOverrideFields.physicalProperties.shippable`
      | `lineItems.${number}.catalogOverrideFields.paymentOption.value`
      | `customLineItems`
      | `customLineItems.${number}.quantity`
      | `customLineItems.${number}.price`
      | `customLineItems.${number}.priceDescription.original`
      | `customLineItems.${number}.itemType.preset`
      | `customLineItems.${number}.itemType.custom`
      | `customLineItems.${number}.paymentOption`
      | `customLineItems.${number}.priceUndetermined`
      | `customLineItems.${number}.fixedQuantity`
      | `customLineItems.${number}.savePaymentMethod`
      | `customLineItems.${number}.membersOnly`
      | `customLineItems.${number}.taxableAddress.addressType`
      | `customContentReference.appId`
      | `customContentReference.componentId`
      | `externalReference.appId`,
      6
    >
  >;
}

export function updateCheckoutTemplate(
  httpClient: HttpClient
): UpdateCheckoutTemplateSignature {
  return (_id: string, checkoutTemplate: UpdateCheckoutTemplate) =>
    universalUpdateCheckoutTemplate(
      _id,
      checkoutTemplate,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCheckoutTemplateSignature {
  /**
   * Updates a checkout template.
   *
   * If the info in a checkout template is updated, only new checkouts created from this template will include the updated items. Checkouts previously
   * created from this template before the update will not be affected.
   * @param - Checkout template ID.
   * @param - Checkout template info to update.
   * @returns Updated checkout template.
   */
  (_id: string, checkoutTemplate: UpdateCheckoutTemplate): Promise<
    NonNullablePaths<
      CheckoutTemplate,
      | `status`
      | `customization.webClient.disabledContinueShoppingButton`
      | `customization.webClient.disabledPolicyAgreementCheckbox`
      | `customization.lockedGiftCard`
      | `customization.lockedCoupon`
      | `customization.disabledManualPayment`
      | `lineItems`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogOverrideFields.productName.original`
      | `lineItems.${number}.catalogOverrideFields.physicalProperties.shippable`
      | `lineItems.${number}.catalogOverrideFields.paymentOption.value`
      | `customLineItems`
      | `customLineItems.${number}.quantity`
      | `customLineItems.${number}.price`
      | `customLineItems.${number}.priceDescription.original`
      | `customLineItems.${number}.itemType.preset`
      | `customLineItems.${number}.itemType.custom`
      | `customLineItems.${number}.paymentOption`
      | `customLineItems.${number}.priceUndetermined`
      | `customLineItems.${number}.fixedQuantity`
      | `customLineItems.${number}.savePaymentMethod`
      | `customLineItems.${number}.membersOnly`
      | `customLineItems.${number}.taxableAddress.addressType`
      | `customContentReference.appId`
      | `customContentReference.componentId`
      | `externalReference.appId`,
      6
    > & {
      __applicationErrorsType?: UpdateCheckoutTemplateApplicationErrors;
    }
  >;
}

export function deleteCheckoutTemplate(
  httpClient: HttpClient
): DeleteCheckoutTemplateSignature {
  return (checkoutTemplateId: string) =>
    universalDeleteCheckoutTemplate(
      checkoutTemplateId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCheckoutTemplateSignature {
  /**
   * Deletes a checkout template.
   *
   * If a checkout template is deleted and a customer attempts to create a checkout with that `checkoutTemplateId` then
   * the customer will be redirected to the domain site.
   * @param - ID of the checkout template to delete.
   */
  (checkoutTemplateId: string): Promise<void>;
}

export function queryCheckoutTemplates(
  httpClient: HttpClient
): QueryCheckoutTemplatesSignature {
  return () =>
    universalQueryCheckoutTemplates(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCheckoutTemplatesSignature {
  /**
   * Creates a query to retrieve a list of checkout templates.
   *
   * The `queryCheckoutTemplates()` function builds a query to retrieve a list of checkout templates and returns a `CheckoutTemplatesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `CheckoutTemplatesQueryBuilder` functions onto the query. `CheckoutTemplatesQueryBuilder` functions enable you to sort, filter, and control the results that `queryCheckoutTemplates()` returns.
   *
   * `queryCheckoutTemplates()` runs with the following `CheckoutTemplatesQueryBuilder` default that you can override:
   * + `ascending("_id")`
   *
   * The functions that are chained to `queryCheckoutTemplates()` are applied in the order they are called. For example, if you apply `ascending("status")` and then `ascending("_id")`, the results are sorted first by the `"status"`, and then, if there are multiple results with the same `"status"`, the items are sorted by `"_id"`.
   *
   * The following `CheckoutTemplatesQueryBuilder` functions are supported for the `queryCheckoutTemplates()` function. For a full description of the checkout template object, see the object returned for the `items` property in `CheckoutTemplatesQueryResult`.
   */
  (): CheckoutTemplatesQueryBuilder;
}

export function typedQueryCheckoutTemplates(
  httpClient: HttpClient
): TypedQueryCheckoutTemplatesSignature {
  return (query: CheckoutTemplateQuery) =>
    universalTypedQueryCheckoutTemplates(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCheckoutTemplatesSignature {
  /** */
  (query: CheckoutTemplateQuery): Promise<
    NonNullablePaths<
      QueryCheckoutTemplatesResponse,
      | `checkoutTemplates`
      | `checkoutTemplates.${number}.status`
      | `checkoutTemplates.${number}.customization.webClient.disabledContinueShoppingButton`
      | `checkoutTemplates.${number}.customization.webClient.disabledPolicyAgreementCheckbox`
      | `checkoutTemplates.${number}.customization.lockedGiftCard`
      | `checkoutTemplates.${number}.customization.lockedCoupon`
      | `checkoutTemplates.${number}.customization.disabledManualPayment`
      | `checkoutTemplates.${number}.customContentReference.appId`
      | `checkoutTemplates.${number}.customContentReference.componentId`
      | `checkoutTemplates.${number}.externalReference.appId`,
      6
    >
  >;
}

export function createCheckoutFromTemplate(
  httpClient: HttpClient
): CreateCheckoutFromTemplateSignature {
  return (checkoutTemplateId: string, siteId: string) =>
    universalCreateCheckoutFromTemplate(
      checkoutTemplateId,
      siteId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCheckoutFromTemplateSignature {
  /**
   * Creates a new checkout based on the checkout template.
   *
   * Before using this method, you must have a checkout template available. Create a checkout template with Create Checkout Template.
   *
   * The customer can be directed to the new checkout using the checkout's `checkoutUrl`.
   * @param - ID of the checkout template to use to create a checkout from.
   * @param - ID of the site associated with the checkout template.
   */
  (checkoutTemplateId: string, siteId: string): Promise<
    NonNullablePaths<
      CreateCheckoutFromTemplateResponse,
      `checkoutId` | `checkoutUrl`,
      2
    > & {
      __applicationErrorsType?: CreateCheckoutFromTemplateApplicationErrors;
    }
  >;
}

export function createAndRedirectToCheckout(
  httpClient: HttpClient
): CreateAndRedirectToCheckoutSignature {
  return (checkoutTemplateId: string, siteId: string) =>
    universalCreateAndRedirectToCheckout(
      checkoutTemplateId,
      siteId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAndRedirectToCheckoutSignature {
  /**
   * Creates a new checkout based on the checkout template and redirects to the new checkout page.
   *
   * Before using this method, you must have a checkout template available. Create a checkout template with Create Checkout Template.
   *
   * To build a URL that uses this method, follow this format:
   * `https://www.wixapis.com/ecom/v1/checkout-templates/{checkoutTemplateId}/create-and-redirect-to-checkout?siteId={siteId}`
   *
   * To create a checkout but not automatically redirect to the checkout page, use Create Checkout From Template.
   * @param - ID of the checkout template to use to create a checkout.
   * @param - ID of the site associated with the checkout template.
   */
  (checkoutTemplateId: string, siteId: string): Promise<
    NonNullablePaths<
      RawHttpResponse,
      `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
      4
    > & {
      __applicationErrorsType?: CreateAndRedirectToCheckoutApplicationErrors;
    }
  >;
}

export const onCheckoutTemplateCreated = EventDefinition(
  'wix.ecom.v1.checkout_template_created',
  true,
  (event: CheckoutTemplateCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.catalogOverrideFields.image' },
            { path: 'entity.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'entity.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'entity.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.customLineItems.serviceProperties.scheduledDate' },
            { path: 'entity.customLineItems.serviceProperties.endDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateCreatedEnvelope>();
export const onCheckoutTemplateDeleted = EventDefinition(
  'wix.ecom.v1.checkout_template_deleted',
  true,
  (event: CheckoutTemplateDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'undefined.lineItems.catalogOverrideFields.image' },
            { path: 'undefined.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'undefined.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'undefined.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'undefined.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'undefined.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            {
              path: 'undefined.customLineItems.serviceProperties.scheduledDate',
            },
            { path: 'undefined.customLineItems.serviceProperties.endDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateDeletedEnvelope>();
export const onCheckoutTemplateUpdated = EventDefinition(
  'wix.ecom.v1.checkout_template_updated',
  true,
  (event: CheckoutTemplateUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.catalogOverrideFields.image' },
            { path: 'entity.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'entity.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'entity.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.customLineItems.serviceProperties.scheduledDate' },
            { path: 'entity.customLineItems.serviceProperties.endDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateUpdatedEnvelope>();
export const onCheckoutTemplateUsed = EventDefinition(
  'wix.ecom.v1.checkout_template_used',
  true,
  (event: CheckoutTemplateUsedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            {
              path: 'data.checkoutTemplate.lineItems.catalogOverrideFields.image',
            },
            { path: 'data.checkoutTemplate.customLineItems.media' },
            { path: 'data.checkout.lineItems.media' },
            { path: 'data.checkout.lineItems.catalogOverrideFields.image' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            {
              path: 'data.checkoutTemplate.customLineItems.physicalProperties.weight',
            },
            { path: 'data.checkout.lineItems.physicalProperties.weight' },
            {
              path: 'data.checkout.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            {
              path: 'data.checkout.appliedDiscounts.merchantDiscount.percentage',
            },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'data.checkoutTemplate.customLineItems.url' },
            { path: 'data.checkout.lineItems.url' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'data.checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            {
              path: 'data.checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
            },
            {
              path: 'data.checkoutTemplate.customLineItems.serviceProperties.endDate',
            },
            { path: 'data.checkout.createdDate' },
            { path: 'data.checkout.updatedDate' },
            {
              path: 'data.checkout.lineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.checkout.lineItems.serviceProperties.scheduledDate' },
            { path: 'data.checkout.lineItems.serviceProperties.endDate' },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.to',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.to',
            },
            {
              path: 'data.checkout.membershipOptions.eligibleMemberships.expirationDate',
            },
            {
              path: 'data.checkout.membershipOptions.invalidMemberships.membership.expirationDate',
            },
            {
              path: 'data.checkout.subscriptionCharges.charges.cycleBillingDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkout.billingInfo.address' },
            { path: 'data.checkout.shippingInfo.shippingDestination.address' },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.pickupDetails.address',
            },
          ],
        },
      ])
    )
)<CheckoutTemplateUsedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdditionalFee,
  AdditionalFeeSource,
  Address,
  AddressLocation,
  AddressWithContact,
  AggregatedTaxBreakdown,
  ApplicableLineItems,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDiscountSourceOneOf,
  AutoTaxFallbackCalculationDetails,
  BalanceType,
  BaseEventMetadata,
  BillingSettings,
  BuyerInfo,
  BuyerInfoIdOneOf,
  CalculationErrors,
  CalculationErrorsShippingCalculationErrorOneOf,
  Carrier,
  CarrierError,
  CarrierErrors,
  CarrierServiceOption,
  CatalogOverrideFields,
  CatalogReference,
  ChannelType,
  Charge,
  ChargeType,
  Checkout,
  CheckoutCustomization,
  CheckoutTemplate,
  CheckoutTemplateCreatedEnvelope,
  CheckoutTemplateDeletedEnvelope,
  CheckoutTemplateQuerySpec,
  CheckoutTemplateUpdatedEnvelope,
  CheckoutTemplateUsed,
  CheckoutTemplateUsedEnvelope,
  CheckoutTemplatesQueryBuilder,
  CheckoutTemplatesQueryResult,
  Color,
  ConversionInfo,
  Coupon,
  CreateAndRedirectToCheckoutRequest,
  CreateCheckoutFromTemplateRequest,
  CreateCheckoutFromTemplateResponse,
  CreateCheckoutTemplateRequest,
  CreateCheckoutTemplateResponse,
  CreatedBy,
  CreatedByIdOneOf,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomContentReference,
  CustomField,
  CustomLineItem,
  CustomSettings,
  DeleteCheckoutTemplateRequest,
  DeleteCheckoutTemplateResponse,
  DeliveryAllocation,
  DeliveryLogistics,
  DeliveryTimeSlot,
  Description,
  DescriptionLine,
  DescriptionLineDescriptionLineValueOneOf,
  DescriptionLineName,
  DescriptionLineType,
  DescriptionLineValueOneOf,
  Details,
  DetailsKindOneOf,
  Dimensions,
  DimensionsUnit,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReference,
  FallbackReason,
  FieldViolation,
  FileType,
  FocalPoint,
  FreeTrialPeriod,
  FullAddressContactDetails,
  GetCheckoutTemplateRequest,
  GetCheckoutTemplateResponse,
  GiftCard,
  GiftCardCalculationError,
  Group,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidMembership,
  ItemAvailabilityInfo,
  ItemAvailabilityStatus,
  ItemCombination,
  ItemCombinationLineItem,
  ItemModifier,
  ItemTaxFullDetails,
  ItemType,
  ItemTypeItemTypeDataOneOf,
  ItemTypePreset,
  JurisdictionType,
  LineItem,
  LineItemDiscount,
  ManualCalculationReason,
  Membership,
  MembershipName,
  MembershipOptions,
  MembershipPaymentCredits,
  MerchantDiscount,
  MessageEnvelope,
  ModifierGroup,
  MultiCurrencyPrice,
  NameInLineItem,
  NameInOther,
  Other,
  OtherCharge,
  PaymentOption,
  PaymentOptionType,
  PhysicalProperties,
  PickupDetails,
  PickupMethod,
  PlainTextValue,
  PlatformFee,
  PlatformFeeChargeType,
  Policy,
  PriceDescription,
  PriceSummary,
  ProductName,
  QueryCheckoutTemplatesRequest,
  QueryCheckoutTemplatesResponse,
  RateType,
  RawHttpResponse,
  Region,
  RestoreInfo,
  RuleType,
  Scope,
  SecuredMedia,
  SelectedCarrierServiceOption,
  SelectedCarrierServiceOptionOtherCharge,
  SelectedCarrierServiceOptionPrices,
  SelectedMembership,
  SelectedMemberships,
  ServiceProperties,
  Severity,
  ShippingInfo,
  ShippingOption,
  ShippingPrice,
  ShippingRegion,
  SortOrder,
  Sorting,
  Status,
  StreetAddress,
  SubscriptionCharges,
  SubscriptionFrequency,
  SubscriptionOptionInfo,
  SubscriptionSettings,
  SuggestedFix,
  SystemError,
  Target,
  TargetLineItem,
  TargetTargetTypeOneOf,
  TaxBreakdown,
  TaxCalculationDetails,
  TaxCalculationDetailsCalculationDetailsOneOf,
  TaxRateBreakdown,
  TaxSummary,
  TaxableAddress,
  TaxableAddressTaxableAddressDataOneOf,
  TaxableAddressType,
  Title,
  TranslatableString,
  UpdateCheckoutTemplate,
  UpdateCheckoutTemplateRequest,
  UpdateCheckoutTemplateResponse,
  V1ItemModifier,
  V1LineItem,
  V1ModifierGroup,
  ValidationError,
  VatId,
  VatType,
  Violation,
  WebClientCustomization,
  WebhookIdentityType,
  WeightUnit,
  utils,
} from './ecom-v1-checkout-template-checkout-templates.universal.js';
